import { useState, useEffect, useCallback } from 'react';
import Header from './components/Header';
import Calendar from './components/Calendar';
import { DatabaseService } from './db/database';
import holidayService from './services/holidayService';
import './App.css';

// Generate random name
const generateRandomName = () => {
  const firstNames = ['James', 'Mary', 'John', 'Patricia', 'Robert', 'Jennifer', 'Michael', 'Linda', 'William', 'Elizabeth'];
  const lastNames = ['Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis', 'Rodriguez', 'Martinez'];
  const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
  const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];
  return `${firstName} ${lastName}`;
};

// Generate random email
const generateRandomEmail = (name) => {
  const domain = ['gmail.com', 'yahoo.com', 'outlook.com', 'example.com'];
  const randomDomain = domain[Math.floor(Math.random() * domain.length)];
  const emailName = name.toLowerCase().replace(/\s+/g, '.');
  const randomNumber = Math.floor(Math.random() * 1000);
  return `${emailName}${randomNumber}@${randomDomain}`;
};

function App() {
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadInitialHolidays = useCallback(async (countryCode) => {
    try {
      const currentYear = new Date().getFullYear();
      console.log(`Loading holidays for ${countryCode} (${currentYear}-${currentYear + 1})...`);

      const holidays = await holidayService.getPublicHolidaysRange(
        countryCode,
        currentYear,
        currentYear + 1
      );

      if (holidays && holidays.length > 0) {
        await DatabaseService.setHolidays(holidays);
        console.log(`Loaded ${holidays.length} holidays for ${countryCode}`);
      } else {
        console.warn(`No holidays returned for ${countryCode}`);
      }
    } catch (error) {
      console.error('Failed to load initial holidays:', error);
      // Don't block app loading if holidays fail
    }
  }, []);

  const ensureHolidaysLoaded = useCallback(async () => {
    try {
      const currentYear = new Date().getFullYear();
      const existingHolidays = await DatabaseService.getHolidaysForYear(currentYear);

      // If no holidays exist for current year, try to load them
      if (!existingHolidays || existingHolidays.length === 0) {
        const userInfo = await DatabaseService.getUserInfo();
        const countryCode = userInfo.countryCode || 'IE';
        console.log(`No holidays found, loading for ${countryCode}...`);
        await loadInitialHolidays(countryCode);
      }
    } catch (error) {
      console.error('Failed to ensure holidays loaded:', error);
      // Don't block app loading if holidays check fails
    }
  }, [loadInitialHolidays]);

  const checkSetup = useCallback(async () => {
    try {
      const setupComplete = await DatabaseService.isSetupComplete();

      if (!setupComplete) {
        // Auto-populate with default values
        const randomName = generateRandomName();
        const randomEmail = generateRandomEmail(randomName);
        const defaultCountry = 'IE'; // Ireland

        await DatabaseService.setUserInfo(randomName, randomEmail, defaultCountry);

        // Load initial holidays for the default country
        await loadInitialHolidays(defaultCountry);
      } else {
        // Check if we need to load holidays
        await ensureHolidaysLoaded();
      }
    } catch (error) {
      console.error('Error checking setup:', error);
    } finally {
      setLoading(false);
    }
  }, [loadInitialHolidays, ensureHolidaysLoaded]);

  useEffect(() => {
    checkSetup();
  }, [checkSetup]);

  const handleSettingsUpdate = () => {
    // Force calendar to refresh by changing key
    setRefreshKey(prev => prev + 1);
  };

  if (loading) {
    return (
      <div className="app-loading">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="app">
      <Header onSettingsUpdate={handleSettingsUpdate} />
      <main className="app-main">
        <Calendar key={refreshKey} />
      </main>
      <footer className="app-footer">
        <p>Office Attendance Tracker - Track your work location with ease</p>
      </footer>
    </div>
  );
}

export default App;
